import { supabase } from "./client";

/**
 * `property_attributes.survey_source` is added by migration
 * 20260626000000_add_survey_source. The self-describing prod_data render path
 * selects this column to decide whether to render from prod_data instead of the
 * legacy `data` tree. In any environment where that migration hasn't run yet,
 * selecting the column makes PostgREST reject the WHOLE query (error 42703,
 * "undefined column") — which would take down readers that fetch survey data
 * alongside unrelated data (e.g. the dashboard, whose asset counts then never
 * render).
 *
 * Probe the column's existence once (memoized for the session) so callers can
 * include it only when present, and otherwise fall back to the legacy `data`
 * path: an absent/null survey_source makes resolveSurveyAttributes use `data`.
 * This makes the readers safe to deploy before the migration is applied — no
 * migrate-first ordering required.
 */
let probe: Promise<boolean> | null = null;

export function hasSurveySourceColumn(): Promise<boolean> {
  if (probe) return probe;
  const p = (async (): Promise<boolean> => {
    const { error } = await supabase
      .from("property_attributes")
      .select("survey_source")
      .limit(1);
    if (error?.code === "42703") return false; // column not migrated yet
    if (error) {
      // Transient/permission error: don't memoize a wrong answer. Assume the
      // column exists and let the caller's own query surface the real error.
      probe = null;
      return true;
    }
    return true;
  })();
  probe = p;
  return p;
}

/**
 * Append `survey_source` to a select column list only when the column exists.
 * Pass the base columns WITHOUT survey_source.
 */
export async function selectWithSurveySource(baseColumns: string): Promise<string> {
  return (await hasSurveySourceColumn()) ? `${baseColumns}, survey_source` : baseColumns;
}
